/**
 * lastSubTabMemory — per-site memory of the last "reports-area" tab.
 *
 * History: this used to remember which of the three Reports SUB-tabs
 * (Generated / Library / Site evidence) the user was last on. SL2b
 * promoted Library + Site evidence to TOP-level tabs ('documents' and
 * 'evidence'), so the memory now tracks which of those three top-level
 * tabs ({Analyses, Decision, Stakeholder Brief, Documents, Site evidence})
 * the user last viewed for a given site — so revisiting a site lands them back in the same
 * place instead of always bouncing to Overview.
 *
 * The reader transparently migrates the legacy `siterag:lastSubTab:`
 * value to the new `siterag:lastSiteTab:` key on first read, so users
 * who had a remembered sub-tab don't land on a dead state.
 *
 * Why localStorage and not Zustand: this is a UX preference that
 * should survive sign-out / page reload; it has no audit trail value
 * and shouldn't roundtrip through Firestore. localStorage is the
 * right scope.
 *
 * Failure mode: storage access throws in Safari private mode and
 * inside some embedded webviews. Every helper swallows those errors —
 * losing the memory is strictly better than crashing the page.
 */

/** The top-level analysis / decision / evidence tabs that make up the reports area. */
export type ReportsAreaTab = 'reports' | 'decision' | 'stakeholder' | 'documents' | 'evidence';

const KEY_PREFIX = 'siterag:lastSiteTab:';
const LEGACY_KEY_PREFIX = 'siterag:lastSubTab:';

/** Map a legacy sub-tab id to its new top-level tab equivalent. */
function migrateLegacyValue(legacy: string | null): ReportsAreaTab | null {
  switch (legacy) {
    case 'generated': return 'reports';
    case 'library':   return 'documents';
    case 'evidence':  return 'evidence';
    // Already a new-shape value persisted by a newer build.
    case 'reports':
    case 'decision':
    case 'stakeholder':
    case 'documents': return legacy;
    default:          return null;
  }
}

function isReportsAreaTab(v: string | null): v is ReportsAreaTab {
  return v === 'reports'
    || v === 'decision'
    || v === 'stakeholder'
    || v === 'documents'
    || v === 'evidence';
}

/**
 * Returns the remembered reports-area tab for this site, or null when
 * there isn't one. On first call it migrates any legacy
 * `siterag:lastSubTab:` value to the new key (and removes the legacy
 * entry) so the dead sub-tab id never lingers.
 */
export function readLastReportsAreaTab(siteId: string): ReportsAreaTab | null {
  try {
    const current = window.localStorage.getItem(KEY_PREFIX + siteId);
    if (isReportsAreaTab(current)) return current;

    // No new-shape value — try to migrate the legacy sub-tab memory.
    const legacy = window.localStorage.getItem(LEGACY_KEY_PREFIX + siteId);
    const migrated = migrateLegacyValue(legacy);
    if (migrated) {
      try {
        window.localStorage.setItem(KEY_PREFIX + siteId, migrated);
        window.localStorage.removeItem(LEGACY_KEY_PREFIX + siteId);
      } catch { /* quota / private mode — fall through with the value */ }
      return migrated;
    }
    return null;
  } catch {
    return null;
  }
}

/** Persists the reports-area tab the user is currently viewing. */
export function writeLastReportsAreaTab(siteId: string, tab: ReportsAreaTab): void {
  try {
    window.localStorage.setItem(KEY_PREFIX + siteId, tab);
  } catch {
    /* quota or private mode — losing the memory is fine */
  }
}
